import {
  Children,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Flex, Text, Tooltip } from '@optiaxiom/react';

type TruncatedListProps = {
  children: ReactNode;
  lineClamp?: number;
  gap?: '0' | '2' | '4' | '6' | '8' | '10' | '12' | '16' | '20' | '24' | '32' | '40' | '48' | '64';
};

const OVERFLOW_INDICATOR_WIDTH = 24;

// Resolve the width we truncate against from a *stable* ancestor rather than the
// content container itself. If we measured the container, collapsing the list
// would shrink it, which re-fires the ResizeObserver and re-expands it — an
// infinite flicker loop (worse the more/larger the items are). A table cell (or
// the parent) is sized by its column, independent of our content, so it never
// oscillates.
const getStableAncestor = (el: HTMLElement): HTMLElement =>
  (el.closest('td,th,[role="cell"],[role="gridcell"]') as HTMLElement | null) ||
  el.parentElement ||
  el;

const getAvailableWidth = (el: HTMLElement): number => {
  const ancestor = getStableAncestor(el);
  const style = window.getComputedStyle(ancestor);
  const padding = parseFloat(style.paddingLeft || '0') + parseFloat(style.paddingRight || '0');
  return ancestor.clientWidth - padding;
};

export function TruncatedList({ children, lineClamp = 1, gap = '6' }: TruncatedListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const childArray = Children.toArray(children);
  const gapPx = parseInt(gap, 10);

  // Stable signature so ordinary parent re-renders (hover, refetch, polling)
  // don't count as a content change and trigger a re-measure.
  const childSignature = childArray
    .map(child =>
      typeof child === 'object' && child !== null && 'key' in child ? String(child.key) : ''
    )
    .join('|');

  // Track the available width from the stable ancestor. We observe that ancestor
  // (not our content), so our own truncation never feeds back into it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ancestor = getStableAncestor(container);
    const update = () => {
      const next = getAvailableWidth(container);
      setAvailableWidth(prev => (next > 0 && next !== prev ? next : prev));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(ancestor);
    return () => observer.disconnect();
  }, []);

  // Measure how many items fit — synchronously before paint, against the stable
  // width. Falls back to showing everything until a width is known, so the cell
  // is never blank.
  useLayoutEffect(() => {
    const container = containerRef.current;
    const measurer = measureRef.current;
    if (!container || !measurer || childArray.length === 0) return;

    const width = availableWidth || getAvailableWidth(container);
    if (width <= 0) return;

    const itemElements = Array.from(measurer.children);
    if (itemElements.length === 0) {
      setVisibleCount(childArray.length);
      return;
    }

    let currentLineWidth = 0;
    let currentLineCount = 1;
    let count = 0;

    for (let i = 0; i < itemElements.length; i++) {
      const itemWidth = (itemElements[i] as HTMLElement).offsetWidth;
      const widthWithGap = itemWidth + (currentLineWidth > 0 ? gapPx : 0);
      const hasMoreItems = i < itemElements.length - 1;
      const isLastAllowedLine = currentLineCount >= lineClamp;
      const reservedSpace =
        hasMoreItems && isLastAllowedLine ? OVERFLOW_INDICATOR_WIDTH + gapPx : 0;

      if (currentLineWidth + widthWithGap + reservedSpace <= width) {
        currentLineWidth += widthWithGap;
        count++;
      } else if (currentLineCount < lineClamp) {
        currentLineCount++;
        currentLineWidth = itemWidth;
        count++;
      } else {
        break;
      }
    }

    const newCount = count || childArray.length;
    setVisibleCount(prev => (prev !== newCount ? newCount : prev));
  }, [availableWidth, childSignature, lineClamp, gapPx, childArray.length]);

  // Until measured, optimistically show everything (in flow, so the cell is
  // never empty). useLayoutEffect collapses before paint, so no visible flash.
  const displayCount = visibleCount ?? childArray.length;
  const remainingCount = childArray.length - displayCount;

  const remainingTooltip = useMemo(() => {
    if (remainingCount === 0) return '';
    return childArray
      .slice(displayCount)
      .map(child => {
        if (typeof child === 'object' && child !== null && 'props' in child) {
          const props = child.props as { children?: ReactNode };
          return typeof props.children === 'string' ? props.children : '';
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
  }, [childArray, displayCount, remainingCount]);

  if (childArray.length === 0) {
    return null;
  }

  return (
    <Flex
      ref={containerRef}
      flexDirection="row"
      gap={gap}
      flexWrap="wrap"
      overflow="hidden"
      flex="1"
      alignItems="center"
    >
      {/* Hidden, out-of-flow measurer used to read each item's natural width. */}
      <Flex
        ref={measureRef}
        aria-hidden
        flexDirection="row"
        gap={gap}
        flexWrap="nowrap"
        style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }}
      >
        {childArray}
      </Flex>
      {childArray.slice(0, displayCount)}
      {remainingCount > 0 && (
        <Tooltip content={remainingTooltip}>
          <Text fontSize="sm" color="fg.secondary" cursor="default">
            +{remainingCount}
          </Text>
        </Tooltip>
      )}
    </Flex>
  );
}
